import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { air, backendUrl, when } from "./api";
import { LEAD_TYPES, FOLLOWS } from "./Filters";

export function LeadDetail({ token, lead, onClose, onSaved }) {
  const [d, setD] = useState(null);
  const [mgr, setMgr] = useState(lead.assigned_manager || "");
  const [status, setStatus] = useState(lead.follow_up_status);
  const [note, setNote] = useState("");
  useEffect(() => { air("leads/get", token, { id: lead.id }).then(setD).catch((e) => toast.error(e.message)); }, [token, lead.id]);
  const save = async () => {
    try { await air("leads/update", token, { id: lead.id, status: status === "CONVERTED" ? null : status, assigned_manager: mgr, note }); toast.success("Lead updated"); onSaved(); onClose(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="air-lead-detail">
        <DialogHeader><DialogTitle>{lead.ref} · {LEAD_TYPES[lead.enquiry_type] || lead.enquiry_type} · {lead.center}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-3 text-xs">
          <div className="space-y-1">
            <p><b>Caller:</b> {lead.caller || "—"} · {when(lead.created_at)}</p>
            <p><b>AI summary:</b> {lead.ai_summary}</p>
            {lead.escalate && <p className="text-red-700"><b>Escalation:</b> {lead.escalation_reason}</p>}
            <p><b>WhatsApp consent:</b> {lead.whatsapp_consent === true ? "Yes" : lead.whatsapp_consent === false ? "No" : "Not asked"}</p>
            {Object.entries(lead.details || {}).map(([k, v]) => <p key={k}><span className="text-muted-foreground">{k.replace(/_/g, " ")}:</span> {String(v)}</p>)}
            <div className="grid grid-cols-2 gap-1 pt-2">
              <Input className="h-8" placeholder="Assigned manager" value={mgr} onChange={(e) => setMgr(e.target.value)} data-testid="air-detail-manager" />
              <select className="h-8 rounded border" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="air-detail-status">{FOLLOWS.filter((s) => s !== "CONVERTED" || lead.booking_id).map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}</select>
              <Input className="h-8 col-span-2" placeholder="Note" value={note} onChange={(e) => setNote(e.target.value)} data-testid="air-detail-note" />
            </div>
          </div>
          <div className="max-h-96 overflow-y-auto space-y-1 rounded bg-muted/30 p-2" data-testid="air-detail-transcript">
            {(d?.transcript || []).map((t, i) => <p key={i} className={t.role === "user" ? "text-right" : ""}><span className="text-muted-foreground">{t.role === "user" ? "Guest" : "AI"}:</span> {t.text}</p>)}
            {d && !d.transcript.length && <p className="text-muted-foreground">Transcript removed by retention policy.</p>}
          </div>
        </div>
        <DialogFooter><Button onClick={save} data-testid="air-detail-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConvertDialog({ token, lead, onClose, onDone }) {
  const [slots, setSlots] = useState([]);
  const [b, setB] = useState({ date: /^\d{4}-\d{2}-\d{2}$/.test(lead.booking_date || "") ? lead.booking_date : "", time_slot: "", num_guests: lead.guests || "", guest_name: lead.guest_name || "", phone: lead.caller || "", special_request: lead.requirement || "" });
  useEffect(() => { fetch(`${backendUrl}/api/bookings/constants`).then((r) => r.json()).then((c) => setSlots(c.time_slots || [])).catch(() => {}); }, []);
  const set = (k, v) => setB({ ...b, [k]: v });
  const go = async () => {
    try { const r = await air("leads/convert", token, { id: lead.id, booking: b }); toast.success(`Booking enquiry ${r.booking_id} created — confirm availability with the guest`); onDone(); onClose(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="air-convert-dialog">
        <DialogHeader><DialogTitle>Convert {lead.ref} to Booking Intelligence</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Creates one booking in Booking Intelligence with status "Enquiry" (source: Phone Call). Confirm it there after checking availability.</p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <label>Date<Input type="date" value={b.date} onChange={(e) => set("date", e.target.value)} data-testid="air-convert-date" /></label>
          <label>Time slot<select className="h-9 w-full rounded border" value={b.time_slot} onChange={(e) => set("time_slot", e.target.value)} data-testid="air-convert-slot"><option value="">Select…</option>{slots.map((s) => <option key={s}>{s}</option>)}</select></label>
          <label>Guests<Input type="number" value={b.num_guests} onChange={(e) => set("num_guests", e.target.value)} data-testid="air-convert-guests" /></label>
          <label>Guest name<Input value={b.guest_name} onChange={(e) => set("guest_name", e.target.value)} data-testid="air-convert-name" /></label>
          <label>Phone<Input value={b.phone} onChange={(e) => set("phone", e.target.value)} data-testid="air-convert-phone" /></label>
          <label>Special request<Input value={b.special_request} onChange={(e) => set("special_request", e.target.value)} data-testid="air-convert-request" /></label>
        </div>
        <DialogFooter><Button onClick={go} disabled={!b.date || !b.time_slot || !b.num_guests || !b.guest_name || !b.phone} data-testid="air-convert-submit">Create booking enquiry</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
